import React, { useEffect, useRef, useState } from "react";
import { DiffEditor, MonacoDiffEditor } from "@monaco-editor/react";
import { Stack, Typography, CircularProgress, Alert } from "@mui/material";
import { useQuery } from "@apollo/client";
import { RECIPE_CONTENT } from "../../graphql/AICopilotQueries";

interface RecipeDiffProps {
  recipeName: string;
  theme: "vs-light" | "vs-dark";
  modifiedContent?: string | null;
}

const RecipeDiff = ({
  recipeName,
  theme,
  modifiedContent,
}: RecipeDiffProps) => {
  const diffEditorRef = useRef<MonacoDiffEditor>(null);
  const [originalValue, setOriginalValue] = useState<string>("");
  const [modifiedValue, setModifiedValue] = useState<string>("");

  // GraphQL query to fetch recipe content (now returns JSON)
  const { data, loading, error, refetch } = useQuery(RECIPE_CONTENT, {
    variables: { name: recipeName },
    skip: !recipeName, // Skip query if no recipe is selected
  });

  useEffect(() => {
    if (data?.recipeContent) {
      try {
        // The original content (left side) - backend now returns JSON directly
        // eslint-disable-next-line react-hooks/set-state-in-effect
        setOriginalValue(data.recipeContent);

        // The modified content (right side)
        if (modifiedContent) {
          // If we have modified content from AI assistant, show it on the right (already JSON)
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setModifiedValue(modifiedContent);
        } else {
          // If no modifications, show the same content on both sides
          // eslint-disable-next-line react-hooks/set-state-in-effect
          setModifiedValue(data.recipeContent);
        }
      } catch (error) {
        console.error("Error processing recipe content:", error);
        const errorMessage = `// Error processing recipe: ${error}`;
        setOriginalValue(errorMessage);
        setModifiedValue(errorMessage);
      }
    } else if (!loading && !error && recipeName) {
      const notFoundMessage = "// Recipe not found";
      setOriginalValue(notFoundMessage);
      setModifiedValue(notFoundMessage);
    } else if (!recipeName) {
      setOriginalValue("");
      setModifiedValue("");
    }
  }, [data, recipeName, loading, error, modifiedContent]);

  // Refetch when recipe name changes
  useEffect(() => {
    if (recipeName) {
      refetch();
    }
  }, [recipeName, refetch]);

  return (
    <Stack height={1}>
      {!recipeName ? (
        <Typography variant="h6" color="text.secondary">
          Select a recipe to edit
        </Typography>
      ) : loading ? (
        <CircularProgress />
      ) : error ? (
        <Alert severity="error">Failed to load recipe: {error.message}</Alert>
      ) : (
        <DiffEditor
          height="100%"
          originalLanguage="json"
          modifiedLanguage="json"
          original={originalValue}
          modified={modifiedValue}
          theme={theme}
          options={{
            minimap: { enabled: false },
            fontSize: 14,
            formatOnPaste: true,
            formatOnType: true,
            renderSideBySide: true,
            wordWrap: "on",
          }}
          onMount={(editor, _) => {
            diffEditorRef.current = editor;
          }}
        />
      )}
    </Stack>
  );
};

export default RecipeDiff;
